import * as React from 'react';
import { CheckedItemStatus, IcheckedItems, IfacilityContact } from '../../models';
import ConfirmModal from '../common/ConfirmModal';
import { useDispatch, useSelector } from 'react-redux';
import { getCompleteJobCheckItems } from '../../reducers/manageInventoryReducer';
import { TFunction } from 'i18next';
import { Button } from 'react-bootstrap';
import { getIsMobile } from '../../reducers/configReducer';
import MobileModalAlt from '../common/mobile/MobileModalAlt';
import { completeJobAPI, sendBackToFSEAPI, sendForReviewAPI, setJobFinalCheckMoal } from '../../actions/manageJobActions';
import {
    addOrUpdateFacilityContact,
    getDeficiencyChapterApplies,
    getReportPreviewInline
} from '../../actions/manageInventoryActions';
import { getSelectedJob } from '../../reducers/commonSelectors';
import finalCheckIcon from '../../images/icons/final-check.svg';
import { constants } from '../../constants/constants';
import { selectOutbox } from '../../reducers/commonReducers';
import { startSync } from '../../actions/commonActions';
import { selectIsSyncRunning } from '../../reducers/syncManagerReducer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEye } from '@fortawesome/free-solid-svg-icons';
import { TOGGLE_MODAL_EDIT_FACILITY_CONTACTS } from '../../actions/actionTypes';
import { securityFunctions } from '../../constants/securityFunctions';
import { IinitialState } from '../../reducers';
import { useMemo } from 'react';
import { SignatureTypeEnum, UserTypeEnum, jobTypesIdEnum } from '../../models-enums';
import { isApproverReviewing } from '../../utils/reviewFlow';
import { toastr } from 'react-redux-toastr';
import { isPopupBlocked, shouldOfferDeficiencyChoice, shouldShowNotTestedToggle } from './jobFinalCheckUtil';
import { applyRealAssetCheckIgnore, isRealAssetCheckFailing, realAssetCheckID } from '../../utils/realAssetCheck';

interface Iprops {
    t: TFunction;
}

export function JobFinalCheckModal(props: Iprops) {
    const [checkList, setCheckList] = React.useState<IcheckedItems[]>([]);
    const completeJobCheckItems = useSelector(getCompleteJobCheckItems);
    const translatedCheckList = useMemo(() => {
        // The list mixes translation keys ("allSAPWOsLinked", "manageInventory:facilityContacts")
        // with ready-made display strings. t() reads everything before a ':' as a namespace, so a
        // literal like "(Required: 2 signatures)" comes back mangled. Keys never contain spaces.
        const translate = (value: string) => (value.includes(' ') ? value : String(props.t(value)));
        return completeJobCheckItems.map(item => ({
            ...item,
            primaryText: translate(item.primaryText),
            secondaryText: translate(item.secondaryText)
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completeJobCheckItems, props.t]);
    const isMobile = useSelector(getIsMobile);
    const job = useSelector(getSelectedJob);
    const [canCloseJob, setCanCloseJob] = React.useState(false);
    const dispatch = useDispatch();
    const outbox = useSelector(selectOutbox);
    const isSyncRunning = useSelector(selectIsSyncRunning);
    const user = useSelector((state: IinitialState) => state.user);
    const jobSignaturesByID = useSelector((state: IinitialState) => state.manageJob.jobSignaturesByID);
    const [closeJobWhenReady, setCloseJobWhenReady] = React.useState(false);
    // Default is N (excluded) per MMG-4262; user opts in to include NOT TESTED assets.
    const [includeNotTested, setIncludeNotTested] = React.useState(false);
    const [isGeneratingPreview, setIsGeneratingPreview] = React.useState(false);
    const [blockedPreviewUrl, setBlockedPreviewUrl] = React.useState<string | undefined>(undefined);
    const showNotTestedToggle = shouldShowNotTestedToggle(job?.jobTypeID);
    // Only inspection/verification jobs are affected; other job types keep today's
    // behavior (include everything), so send true when the toggle isn't shown.
    const includeNotTestedAssets = showNotTestedToggle ? includeNotTested : true;
    // MMG-4513: repair jobs ask "Include Deficiency / Repair page in report?" only when the job's own assets
    // would print on it. undefined = not answered by the server yet; a failed check asks anyway (default Yes).
    const offerDeficiencyChoice = shouldOfferDeficiencyChoice(job?.jobTypeID);
    const [deficiencyApplies, setDeficiencyApplies] = React.useState<boolean | undefined>(undefined);
    const [includeDeficiency, setIncludeDeficiency] = React.useState(true);
    const showDeficiencyRow = offerDeficiencyChoice && deficiencyApplies === true;
    const deficiencyCheckPending = offerDeficiencyChoice && deficiencyApplies === undefined;
    const includeDeficiencyChapters = offerDeficiencyChoice ? !showDeficiencyRow || includeDeficiency : undefined;
    // MMG-4543: lets the FSE close a job whose work has no real asset; local so it resets each time Final Check opens
    const [ignoreRealAssetCheck, setIgnoreRealAssetCheck] = React.useState(false);
    const showIgnoreRealAssetCheck = isRealAssetCheckFailing(completeJobCheckItems);

    const isFSEApprovalFlow = user?.type === UserTypeEnum.FSEApprovalFlow;
    const isInspectionJob = job?.jobTypeID === jobTypesIdEnum.inspection;
    const requiresReview = isFSEApprovalFlow && isInspectionJob;
    const isApproverReview = isApproverReviewing(job, user);
    const hasApproverSignature = Object.values(jobSignaturesByID).some(
        sig => sig.jobID === job?.id && sig.signatureType === SignatureTypeEnum.Approver
    );

    const onOk = () => {
        setCloseJobWhenReady(false);
        dispatch(setJobFinalCheckMoal(false));
        if (requiresReview) {
            dispatch(sendForReviewAPI(job, includeNotTestedAssets));
        } else {
            const jobToClose = { ...job, status: 'Completed' };
            dispatch(completeJobAPI(jobToClose, undefined, includeNotTestedAssets, includeDeficiencyChapters));
        }
    };

    const onSendBackToFSE = () => {
        dispatch(setJobFinalCheckMoal(false));
        const toastrConfirmOptions = {
            onOk: () => {
                dispatch(sendBackToFSEAPI(job));
            },
            onCancel: () => null,
            okText: String(props.t('common:ok')),
            cancelText: String(props.t('common:cancel'))
        };
        toastr.confirm(String(props.t('nsJob:sendBackConfirmation')), toastrConfirmOptions);
    };

    const close = () => {
        setCloseJobWhenReady(false);
        dispatch(setJobFinalCheckMoal(false));
    };

    /*
     * MMG-4296/4297: progress and failures stay on this modal — we no longer pre-open a
     * blank tab to hold status text. The tab is opened only once the PDF is in hand, which
     * puts it outside the click gesture, so a popup blocker can swallow it; when that
     * happens we hand the FSE a link to click instead of failing silently.
     */
    const handleGetReportPreview = () => {
        if (!job?.id || isGeneratingPreview) {
            return;
        }

        setIsGeneratingPreview(true);
        setBlockedPreviewUrl(undefined);

        const result = dispatch(getReportPreviewInline(job.id, includeNotTestedAssets, includeDeficiencyChapters)) as any;

        return Promise.resolve(result)
            .then((objectUrl: string | undefined) => {
                // A missing URL means the action already explained why (no preview
                // available, or the request failed) via toastr over this modal.
                if (objectUrl && isPopupBlocked(window.open(objectUrl, '_blank'))) {
                    setBlockedPreviewUrl(objectUrl);
                }
            })
            .catch((error: any) => {
                // The action handles its own request failures, so reaching here means
                // opening the tab itself failed. Don't leave the spinner spinning.
                constants.handleError(error, 'preview report');
                console.error('[handleGetReportPreview]:', error);
            })
            .finally(() => {
                setIsGeneratingPreview(false);
            });
    };

    const syncOutbox = () => {
        if (canCloseJob) setCloseJobWhenReady(true);

        dispatch(startSync());
    };

    const anyPendingOutboxItems = () => {
        if (outbox.length > 0) {
            return true;
        }

        return false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        // When the component unmounts, make sure the modal is closed
        // we had a live bug where users somehow left the page, maybe refreshed the browser? Or closed the tab and opened a new one?
        // and this value never got reset.
        return () => {
            dispatch(setJobFinalCheckMoal(false));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const jobID = job?.id;
    React.useEffect(() => {
        if (!offerDeficiencyChoice || !jobID) {
            return undefined;
        }
        // results still in the outbox are not on the server yet; ask again once the sync empties it
        if (isSyncRunning || outbox.length > 0) {
            if (outbox.length > 0) setDeficiencyApplies(undefined);
            return undefined;
        }
        let cancelled = false;
        getDeficiencyChapterApplies(jobID)
            .then(applies => {
                if (!cancelled) setDeficiencyApplies(applies);
            })
            .catch((error: any) => {
                console.error('[getDeficiencyChapterApplies]:', error);
                if (!cancelled) setDeficiencyApplies(true);
            });
        return () => {
            cancelled = true;
        };
    }, [offerDeficiencyChoice, jobID, isSyncRunning, outbox.length]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (closeJobWhenReady && !isSyncRunning && !anyPendingOutboxItems() && !deficiencyCheckPending) {
            // MMG-4513: the deficiency question only appears after the sync; stop so the engineer answers it
            if (showDeficiencyRow) {
                setCloseJobWhenReady(false);
                return;
            }
            // User has clicked to close the job after syncing the outbox, let's close the modal and the job
            onOk();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSyncRunning, outbox, deficiencyApplies]);

    React.useEffect(() => {
        let canClose = true;

        applyRealAssetCheckIgnore(completeJobCheckItems, ignoreRealAssetCheck).forEach(item => {
            if (item.applicable && !item.optional && item.passed === CheckedItemStatus.fail) {
                canClose = false;
            }
        });

        if (anyPendingOutboxItems()) {
            canClose = false;
        }

        const syncChecklist: IcheckedItems = {
            primaryText: props.t('manageInventory:finalChecklist.pendingChangesSynced'),
            secondaryText: props.t('manageInventory:finalChecklist.mandatory'),
            passed: anyPendingOutboxItems() ? CheckedItemStatus.fail : CheckedItemStatus.pass,
            optional: false,
            applicable: true
        };

        const additionalItems: IcheckedItems[] = [syncChecklist];

        // Approver-signature requirement only applies while an inspection job is
        // actually under review by its approver (see utils/reviewFlow).
        if (isApproverReview) {
            if (!hasApproverSignature) {
                canClose = false;
            }
            additionalItems.push({
                primaryText: props.t('manageInventory:finalChecklist.approverSignatureRequired'),
                secondaryText: props.t('manageInventory:finalChecklist.mandatory'),
                passed: hasApproverSignature ? CheckedItemStatus.pass : CheckedItemStatus.fail,
                optional: false,
                applicable: true
            });
        }

        setCheckList([...additionalItems, ...applyRealAssetCheckIgnore(translatedCheckList, ignoreRealAssetCheck)]);
        setCanCloseJob(canClose);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completeJobCheckItems, outbox, hasApproverSignature, ignoreRealAssetCheck]);

    const getValitiyClass = (item: IcheckedItems): string => {
        if (item.contacts) {
            return 'neutral';
        }
        switch (item.passed) {
            case CheckedItemStatus.fail: {
                return 'invalid';
            }
            case CheckedItemStatus.pass: {
                return 'valid';
            }
            default: {
                return 'neutral';
            }
        }
    };

    const getApplicableClass = (item: IcheckedItems) =>
        item.applicable || (!item.applicable && item.passed === CheckedItemStatus.pass) ? 'applicable' : 'inapplicable';

    const getItemClass = (item: IcheckedItems) => {
        return `${getValitiyClass(item)} ${getApplicableClass(item)}`;
    };

    const mobileTilteStyle: any = {
        background: 'var(--blues-blue-main, #007BC3)',
        color: 'var(--white, #FFF)',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    };

    const title = (
        <div style={isMobile ? mobileTilteStyle : {}} className="title">
            <img style={{ width: '40px', height: '40px', flexShrink: 0 }} src={finalCheckIcon} />
            <span>{String(props.t('nsJob:finalCheck'))} </span>
        </div>
    );

    const buttons = (
        <>
            <Button onClick={close}>{String(props.t('common:cancel'))}</Button>

            {isSyncRunning && <FontAwesomeIcon style={{ marginLeft: '16px' }} icon={faSpinner} spin={true} size="lg" />}

            {anyPendingOutboxItems() && !isSyncRunning && (
                <Button
                    style={{ marginLeft: '24px' }}
                    variant="primary"
                    onClick={() => {
                        syncOutbox();
                    }}
                >
                    {canCloseJob ? String(props.t('common:syncAndClose')) : String(props.t('common:sync'))}
                </Button>
            )}

            {isApproverReview && !anyPendingOutboxItems() && !isSyncRunning && (
                <Button
                    style={{ marginLeft: '24px' }}
                    variant="warning"
                    onClick={onSendBackToFSE}
                >
                    {String(props.t('nsJob:sendBackToFSE'))}
                </Button>
            )}

            {!anyPendingOutboxItems() && !isSyncRunning && (
                <Button
                    style={{ marginLeft: '24px' }}
                    variant="primary"
                    disabled={!canCloseJob || deficiencyCheckPending}
                    onClick={() => {
                        onOk();
                    }}
                >
                    {requiresReview
                        ? String(props.t('nsJob:sendForReview'))
                        : String(props.t('nsJob:closeJob'))}
                </Button>
            )}
        </>
    );

    const mobileFooter = <div className={'form-buttons'}>{buttons}</div>;

    const webFooter = (
        <div className="footer" style={{ textAlign: 'center' }}>
            <p style={{ paddingBottom: '24px' }}>{String(props.t('manageInventory:finalJobCheckQuestion'))}</p>
            {buttons}
        </div>
    );

    const editContactsAllowed = constants.hasSecurityFunction(user, securityFunctions.FSEFacilityContactEdit.id);

    const openEditContacts = (e: React.MouseEvent) => {
        e.preventDefault();
        close();
        dispatch({ type: TOGGLE_MODAL_EDIT_FACILITY_CONTACTS });
    };

    const contactDisplay = (contact: IfacilityContact) =>
        `${contact.user?.first || contact.firstName} ${contact.user?.last || contact.lastName}; ${contact.user?.email ||
            contact.email}`;

    const setContactNotifications = (contact: IfacilityContact, next: boolean) => {
        if (contact.enableNotifications === next) {
            return;
        }
        dispatch(addOrUpdateFacilityContact({ ...contact, enableNotifications: next }, job?.facilityID || '', false));
    };

    // MMG-4370: mutually-exclusive report options render as Yes/No radios instead of toggles.
    const yesNoRadios = (name: string, value: boolean, onChange: (next: boolean) => void) => (
        <div className="yes-no-radios">
            <label>
                <span>{String(props.t('common:yes'))}</span>
                <input type="radio" name={name} checked={value} onChange={() => onChange(true)} />
                <span className="radio-mark" />
            </label>
            <label>
                <span>{String(props.t('common:no'))}</span>
                <input type="radio" name={name} checked={!value} onChange={() => onChange(false)} />
                <span className="radio-mark" />
            </label>
        </div>
    );

    const contactsItem = checkList.find(item => item.contacts);
    const checklistItems = checkList.filter(item => !item.contacts);
    const contacts = contactsItem?.contacts ?? [];
    // With one contact the radios sit on the question row (per Figma); with several,
    // each contact keeps its own Yes/No pair. Mobile always stacks radios under the contact.
    const singleContact = contacts.length === 1 ? contacts[0] : undefined;

    const content = (
        <div className="content">
            <h4 className="final-check-section-header">{String(props.t('nsJob:checklist'))}</h4>
            <ul>
                {checklistItems.map(item => (
                    <React.Fragment key={item.primaryText}>
                        <li className={getItemClass(item)}>
                            <span> {item.primaryText}</span>
                            {item.secondaryText && <span className="secondary-text"> {item.secondaryText}</span>}
                        </li>
                        {item.id === realAssetCheckID && showIgnoreRealAssetCheck && (
                            <li className="checklist-choice">
                                <span>{String(props.t('manageInventory:finalChecklist.ignoreRealAssetCheck'))}</span>
                                {yesNoRadios('ignore-real-asset-check', ignoreRealAssetCheck, setIgnoreRealAssetCheck)}
                            </li>
                        )}
                    </React.Fragment>
                ))}
            </ul>
            <div className="final-check-report-settings">
                <h4 className="final-check-section-header">{String(props.t('nsJob:reportSettings'))}</h4>
                {contactsItem && (
                    <div className="report-setting">
                        <div className="report-setting-row">
                            <span className="report-setting-label">{String(props.t('nsJob:notifyFacilityManager'))}</span>
                            {!isMobile &&
                                singleContact &&
                                yesNoRadios('notify-contact', singleContact.enableNotifications, next =>
                                    setContactNotifications(singleContact, next)
                                )}
                        </div>
                        {contacts.map((contact, index) => (
                            <div className="report-setting-row contact-row" key={index}>
                                {editContactsAllowed ? (
                                    <a href="/" onClick={openEditContacts}>
                                        {contactDisplay(contact)}
                                    </a>
                                ) : (
                                    <span className="contact-text">{contactDisplay(contact)}</span>
                                )}
                                {(isMobile || !singleContact) &&
                                    yesNoRadios(`notify-contact-${index}`, contact.enableNotifications, next =>
                                        setContactNotifications(contact, next)
                                    )}
                            </div>
                        ))}
                        {contacts.length === 0 && editContactsAllowed && (
                            <div className="report-setting-row">
                                <a href="/" onClick={openEditContacts}>
                                    {String(props.t('manageInventory:editFacilitycontacts'))}
                                </a>
                            </div>
                        )}
                    </div>
                )}
                {showNotTestedToggle && (
                    <div className="report-setting">
                        <div className="report-setting-row">
                            <span className="report-setting-label">
                                {String(props.t('nsJob:includeNotTestedAssets'))}
                            </span>
                            {yesNoRadios('include-not-tested', includeNotTested, setIncludeNotTested)}
                        </div>
                    </div>
                )}
                {showDeficiencyRow && (
                    <div className="report-setting">
                        <div className="report-setting-row">
                            <span className="report-setting-label">
                                {String(props.t('nsJob:includeDeficiencyReport'))}
                            </span>
                            {yesNoRadios('include-deficiency-report', includeDeficiency, setIncludeDeficiency)}
                        </div>
                    </div>
                )}
                {(showNotTestedToggle || showDeficiencyRow) && job?.id && (
                    <div className="report-preview">
                        <Button
                            variant="outline-secondary"
                            className="report-preview-button"
                            disabled={isGeneratingPreview}
                            onClick={handleGetReportPreview}
                        >
                            <FontAwesomeIcon
                                icon={isGeneratingPreview ? faSpinner : faEye}
                                spin={isGeneratingPreview}
                                style={{ marginRight: '8px' }}
                            />
                            {isGeneratingPreview
                                ? String(props.t('nsJob:generatingReportPreview'))
                                : String(props.t('nsJob:getReportPreview'))}
                        </Button>
                        {blockedPreviewUrl && (
                            <p style={{ margin: '12px 0 0' }}>
                                {String(props.t('nsJob:reportPreviewBlocked'))}{' '}
                                <a href={blockedPreviewUrl} target="_blank" rel="noopener noreferrer">
                                    {String(props.t('nsJob:openReportPreview'))}
                                </a>
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    if (!isMobile) {
        return (
            <>
                <div className="backdrop">
                    <ConfirmModal>
                        <div className="modal-content final-check">
                            {title}
                            {content}
                            {webFooter}
                        </div>
                    </ConfirmModal>
                </div>
            </>
        );
    } else {
        return (
            <MobileModalAlt show={true} onHide={close} className="" title={title} headerStyle={{ padding: '0px' }}>
                <div className="modal-content final-check final-check-mobile">
                    {content}
                    {mobileFooter}
                </div>
            </MobileModalAlt>
        );
    }
}
